import { Download, UploadFile } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DialogContent, Grid, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { FileSection } from "components/atoms";
import { renderProgress } from "components/atoms/PercentProgressCell";
import {
  AppTable,
  ImportDocumentDialog,
  ImportErrorDialog,
} from "components/molecules";
import { TableHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import { isArray, isEmpty, isUndefined } from "lodash";
import {
  EImportType,
  IDownloadMaterialRequisitionReportRequest,
  IGetAllMaterialRequirementRequest,
  IImportMaterialRequirementRequest,
  IMaterialRequirement,
  IPaginateApiResponse,
  IPaginationRequest,
} from "model";
import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  DownloadActions,
  LoadingOrderActions,
  MaterialRequirementActions,
} from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { MaterialRequirementApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import FilterPopup from "./components/FilterPopup";

enum ERequestType {
  BY_STOCKER,
  BY_PRODUCTION,
}

const _fileId = "import-material-requirement";

const MaterialRequirement = () => {
  const fRef = React.useRef<any>();
  const dispatch = useDispatch();
  const history = useHistory();
  const action = useAppSelector(
    (state) => state.MaterialRequirementReducer.action
  );
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const errors = useAppSelector((state) => state.ErrorReducer.errors);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );
  const [refresh, setRefresh] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [importRequestType, setImportRequestType] = useState(0);

  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    searchText: searchText,
  });
  const [file, setFile] = useState<FileList | null>(null);
  const [currentItem, setCurrentItem] = useState<
    IMaterialRequirement | undefined
  >(undefined);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [open, setOpen] = useState<boolean>(false);

  const [importFailedDialogVisible, setImportFailedDialogVisible] =
    useState<boolean>(false);

  const [importType, setImportType] = useState<EImportType>(
    EImportType.IMPORT_NEW
  );
  const [requestType, setRequestType] = useState<ERequestType>(
    ERequestType.BY_STOCKER
  );
  const isImportNewDocs = importType === EImportType.IMPORT_NEW;

  const onCellClick = (event: any) => {
    if (event.colDef.field !== "action") {
      history.push(
        `/receive/material-requirement/${event.row.id}&${event.row.documentNbr}`
      );
    }
  };

  const clearFilePath = () => {
    if (fRef.current) {
      fRef.current.value = "";
    }
  };

  const handleSelectFile = (files: FileList | null) => {
    if (!isEmpty(files)) {
      setImportType(importType);
      setFile(files);
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setOpen(false);
    setCurrentItem(undefined);
  };

  const { loading, result } = usePaginationRequest<
    IGetAllMaterialRequirementRequest,
    any[]
  >(
    request,
    MaterialRequirementApiService.getAllMaterialRequirement,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );
  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _handleImportRequestDocument = () => {
    const request: IImportMaterialRequirementRequest = {
      file: file?.item(0) as File,
      requestMaterialId: currentItem?.id || "",
    };
    switch (importRequestType) {
      case 0:
        _importMaterialRequirementDocument(request);
        break;
      case 1:
        _importMoveMaterialDocument(request);
        break;
      case 2:
        _importMoveBagDocument(request);
        break;
    }
  };
  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const _importMaterialRequirementDocument = (
    request: IImportMaterialRequirementRequest
  ) => {
    dispatch(MaterialRequirementActions.importMaterial.request(request));
  };

  const _importMoveMaterialDocument = (
    request: IImportMaterialRequirementRequest
  ) => {
    dispatch(MaterialRequirementActions.importMoveMaterial.request(request));
  };
  const _importMoveBagDocument = (
    request: IImportMaterialRequirementRequest
  ) => {
    dispatch(MaterialRequirementActions.importMoveBag.request(request));
  };

  const importDocsSuccess =
    action.includes(MaterialRequirementActions.importMaterial.successName) ||
    action.includes(MaterialRequirementActions.importMoveBag.successName) ||
    action.includes(MaterialRequirementActions.importMoveMaterial.successName);

  const importDocsFailed =
    action.includes(MaterialRequirementActions.importMaterial.failedName) ||
    action.includes(MaterialRequirementActions.importMoveBag.failedName) ||
    action.includes(MaterialRequirementActions.importMoveMaterial.failedName);

  const _downloadMaterialRequisition = () => {
    const params: IDownloadMaterialRequisitionReportRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<any[]>).totalRecords,
      pageNumber: FIRST_PAGE,
      fileName: EXPORT_FILE_NAME.materialRequisitionForm,
    };
    dispatch(
      DownloadActions.downloadMaterialRequisitionReportRequest.request(params)
    );
  };

  useEffect(() => {
    if (importDocsSuccess) {
      handleCancel();
      setRefresh(true);
    }
    if (importDocsFailed) {
      if (isArray(errors) || !isUndefined(errors)) {
        setOpen(false);
        setRefresh(true);

        // setImportFailedDialogVisible(true);
      }
    }
  }, [action]);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "documentNbr",
      headerName: I18nTranslator(translations.documentCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "date",
      headerName: I18nTranslator(translations.masterData.importDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    // TableHelper.createCell({
    //   field: "status",
    //   headerName: I18nTranslator(translations.status),
    //   align: "left",
    //   size: "small",
    //   renderCell: ({ value }: { value: ELoadingOrderStatus }) => (
    //     <StatusCell
    //       bgcolor={ColorHelper.getLoadingOrderStatusColor(
    //         value as ELoadingOrderStatus
    //       )}
    //     >
    //       {StatusHelper.getLOStatusString(value)}
    //     </StatusCell>
    //   ),
    // }),
    TableHelper.createCell({
      field: "planningQuantity",
      headerName: I18nTranslator(
        translations.materialRequisition.totalRequestKGQuantity
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "bookedQuantity",
      headerName: I18nTranslator(
        translations.materialRequisition.totalBookedKGQuantity
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "preparedQuantity",
      headerName: I18nTranslator(
        translations.materialRequisition.totalPrepareKGQuantity
      ),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "deliveredQuantity",
      headerName: I18nTranslator(
        translations.materialRequisition.totalDeliveryKGQuantity
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "preparePalletQuantity",
      headerName: I18nTranslator(
        translations.materialRequisition.totalPreparePalletQuantity
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "deliveryPalletQuantity",
      headerName: I18nTranslator(
        translations.materialRequisition.totalDeliveryPalletQuantity
      ),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "prepareProgress",
      headerName: I18nTranslator(
        translations.materialRequisition.prepareProgress
      ),
      size: "small",
      align: "left",
      renderCell: renderProgress,
    }),
    TableHelper.createCell({
      field: "deliveryProgress",
      headerName: I18nTranslator(
        translations.materialRequisition.exportProgress
      ),
      size: "small",
      align: "left",
      renderCell: renderProgress,
    }),
    TableHelper.createCell({
      field: "description",
      headerName: I18nTranslator(translations.receive.description),
      size: "full",
      align: "left",
    }),
  ];

  return (
    <Fragment>
      <FileSection ref={fRef} id={_fileId} onFileSelected={handleSelectFile} />
      <AppTable<IMaterialRequirement>
        title={I18nTranslator(translations.routes.materialRequirements)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onCellClick={onCellClick}
        onSearch={handleSearch}
        searchPlaceholder={"Tìm kiếm: Mã phiếu"}
        onChangePage={handleChangePage}
        onFileSelected={handleSelectFile}
        actionColumnProps={{
          hideDelete: true,
          hideEdit: true,
        }}
        hideAddNew
        onRowPressImport={(row) => {
          setImportType(EImportType.UPDATE);
          setCurrentItem(row);
        }}
        isDownloading={downloading}
        onDownloadClick={_downloadMaterialRequisition}
        onPageSizeChange={_handlePageSizeChange}
        headerButtonsLeft={
          <Stack direction={"row"} spacing={2}>
            <label htmlFor={_fileId}>
              <LoadingButton
                startIcon={<UploadFile />}
                loading={false}
                variant="contained"
                component={"span"}
                onClick={() => {
                  setImportType(EImportType.IMPORT_NEW);
                  setImportRequestType(0);
                }}
              >
                {I18nTranslator(translations.button.importRequestDocs)}
              </LoadingButton>
            </label>
            <label htmlFor={_fileId}>
              <LoadingButton
                startIcon={<UploadFile />}
                loading={false}
                variant="contained"
                component={"span"}
                onClick={() => {
                  setImportType(EImportType.IMPORT_NEW);
                  setImportRequestType(1);
                }}
              >
                {I18nTranslator(
                  translations.button.importMaterialRequisitonDocs
                )}
              </LoadingButton>
            </label>
            <label htmlFor={_fileId}>
              <LoadingButton
                startIcon={<UploadFile />}
                loading={false}
                variant="contained"
                component={"span"}
                onClick={() => {
                  setImportType(EImportType.IMPORT_NEW);
                  setImportRequestType(2);
                }}
              >
                {I18nTranslator(
                  translations.button.importPackagingRequisitonDocs
                )}
              </LoadingButton>
            </label>
          </Stack>
        }
        headerButtonsRight={
          <Stack
            direction={"row"}
            spacing={2}
            sx={{ marginLeft: "10px", marginTop: "6px" }}
          >
            <LoadingButton
              startIcon={<Download />}
              loading={downloading}
              variant="contained"
              component={"span"}
              onClick={handleExportClick}
            >
              {I18nTranslator(translations.button.exportTemplate)}
            </LoadingButton>
          </Stack>
        }
        hideImport
      />
      <ImportDocumentDialog
        title={I18nTranslator(
          isImportNewDocs
            ? translations.receive.importDocs
            : translations.receive.updateDocs
        )}
        open={open}
        handleConfirm={_handleImportRequestDocument}
        handleClose={handleCancel}
        loading={action.includes(
          LoadingOrderActions.importLoadingOrderDocument.requestName
        )}
        fileName={!isEmpty(file) && file ? file[0].name : ""}
        isImportNewDocs={isImportNewDocs}
        updateItemName={currentItem?.documentNbr || ""}
      />
      <ImportErrorDialog
        title={I18nTranslator(translations.receive.importError)}
        open={importFailedDialogVisible}
        handleReImport={() => {
          setImportFailedDialogVisible(false);
          handleCancel();
        }}
      >
        <DialogContent sx={{ maxHeight: "50vh" }}>
          {!isUndefined(errors) &&
            isArray(errors) &&
            (errors as string[]).map((error) => {
              return <Typography>{error}</Typography>;
            })}
        </DialogContent>
      </ImportErrorDialog>
      <FilterPopup anchor={anchorEl} onClose={() => setAnchorEl(null)} />
    </Fragment>
  );
};

export default MaterialRequirement;
